import { Fragment, useMemo, useState, type DragEvent } from 'react';
import {
  Button,
  Dropdown,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tag,
  Toggle,
  Tooltip,
} from '@carbon/react';
import { Draggable, Information, TrashCan } from '@carbon/react/icons';
import classNames from 'classnames';
import {
  groupsOf,
  includedOf,
  statusOf,
  type NameGroup,
  type WellConsolidationAction,
  type WellConsolidationState,
} from './consolidation-utils';
import { WellConsolidationSurvivors } from './well-consolidation-survivors';
import styles from './well-consolidation-panel.module.scss';

type SortMode = 'alpha-asc' | 'alpha-desc';

const SORT_ITEMS: { id: SortMode; label: string }[] = [
  { id: 'alpha-asc', label: 'Name (A → Z)' },
  { id: 'alpha-desc', label: 'Name (Z → A)' },
];

interface ConsolidationRowProps {
  state: WellConsolidationState;
  dispatch: (action: WellConsolidationAction) => void;
  onRequestRemove: (key: string) => void;
  group: NameGroup;
  /** A removed name revealed for restoring. */
  hidden?: boolean;
  /** Whether a drag is hovering this (survivor) row. */
  dropOver: boolean;
  onDragOverChange: (key: string | null) => void;
  onDrop: (survivorKey: string, event: DragEvent) => void;
}

/**
 * One consolidation row: the Survivor toggle, the name and its role, how many of its
 * records are included, and the remove (or restore) action. Non-survivor rows drag;
 * survivor rows take the drop.
 * @param {ConsolidationRowProps} props The model, the row's name and its handlers.
 * @return {JSX.Element} The table row.
 */
function ConsolidationRow({
  state,
  dispatch,
  onRequestRemove,
  group,
  hidden = false,
  dropOver,
  onDragOverChange,
  onDrop,
}: ConsolidationRowProps) {
  const key = group.key;
  const status = statusOf(state, key);
  const isSurvivor = status === 'survivor';
  const draggable = !isSurvivor && !hidden;
  const included = includedOf(state, key).length;
  const total = group.members.length;

  const dragProps = draggable
    ? {
        onDragStart: (event: DragEvent) =>
          event.dataTransfer.setData('text/plain', key),
        onDragEnd: () => onDragOverChange(null),
      }
    : {};
  const dropProps = isSurvivor
    ? {
        onDragOver: (event: DragEvent) => {
          event.preventDefault();
          onDragOverChange(key);
        },
        onDragLeave: () => onDragOverChange(null),
        onDrop: (event: DragEvent) => onDrop(key, event),
      }
    : {};

  return (
    <tr
      className={classNames(styles.row, {
        [styles.survivorRow]: isSurvivor,
        [styles.dedupedRow]: status === 'dependent',
        [styles.hiddenRow]: hidden,
        [styles.dropTarget]: dropOver,
      })}
      draggable={draggable}
      {...dragProps}
      {...dropProps}
    >
      <td className={styles.survivorCell}>
        <Toggle
          id={`reconcile-survivor-${key}`}
          size="sm"
          labelText=""
          labelA=""
          labelB=""
          hideLabel
          toggled={isSurvivor}
          onToggle={(on: boolean) => {
            if (on) dispatch({ type: 'toggleSurvivor', key });
            else onRequestRemove(key);
          }}
          aria-label={`Survivor toggle for ${group.name}`}
        />
      </td>
      <td className={styles.nameCell}>
        <span className={styles.nameMain}>
          {isSurvivor && (
            <span className={styles.badges}>
              <Tag type="high-contrast" size="sm">
                {`Survivor ${state.survivorKeys.indexOf(key) + 1}`}
              </Tag>
            </span>
          )}
          <span className={styles.nameLine}>
            {status === 'dependent' && (
              <Draggable size={16} className={styles.dragHandle} aria-hidden />
            )}
            <span className={styles.name}>{group.name}</span>
          </span>
        </span>
        {/* One "Shut In · 12023042250000" line per well under the name, whatever is included. */}
        {group.members.map((well) => (
          <span key={well.rowId} className={styles.subtle}>
            {well.wellStatus} · {well.uwi14 ?? '—'}
          </span>
        ))}
      </td>
      <td className={styles.countCell}>
        <span className={styles.count}>
          {included} of {total} included
        </span>
        {included < total && status !== 'unactioned' && (
          <Button
            kind="ghost"
            size="sm"
            className={styles.includeAll}
            onClick={() => dispatch({ type: 'includeAll', key })}
          >
            Include all
          </Button>
        )}
      </td>
      <td className={styles.actionsCell}>
        {hidden ? (
          <Button
            kind="ghost"
            size="sm"
            onClick={() => dispatch({ type: 'restoreName', key })}
          >
            Restore
          </Button>
        ) : (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={TrashCan}
            iconDescription="Remove"
            tooltipPosition="left"
            onClick={() => onRequestRemove(key)}
          />
        )}
      </td>
    </tr>
  );
}

interface ConsolidationHeaderProps {
  distinctCount: number;
  unactionedCount: number;
  search: string;
  onSearch: (search: string) => void;
  sortMode: SortMode;
  onSort: (mode: SortMode) => void;
}

/**
 * The panel heading with its live count chip, and the search/sort toolbar flush above
 * the table.
 * @param {ConsolidationHeaderProps} props The counts and the toolbar state.
 * @return {JSX.Element} The header and toolbar.
 */
function ConsolidationHeader({
  distinctCount,
  unactionedCount,
  search,
  onSearch,
  sortMode,
  onSort,
}: ConsolidationHeaderProps) {
  return (
    <>
      <div className={styles.header}>
        <h4 className={styles.heading}>
          Well Consolidation
          <Tooltip
            align="bottom-start"
            label="The survivor/dedupe structure this reconcile commits. Toggle a name on as a survivor, or check records on the left to fold them into the most recent survivor."
          >
            <button
              type="button"
              className={styles.infoBtn}
              aria-label="How to use Well Consolidation"
            >
              <Information size={16} />
            </button>
          </Tooltip>
          <Tag type="cool-gray" size="sm" className={styles.countChip}>
            {distinctCount} distinct names, {unactionedCount} unactioned
          </Tag>
        </h4>
      </div>

      <TableToolbar
        size="sm"
        className={styles.toolbar}
        aria-label="Consolidation table toolbar"
      >
        <TableToolbarContent>
          <TableToolbarSearch
            persistent
            placeholder="Search distinct names"
            labelText="Search distinct names"
            value={search}
            onChange={(event) =>
              onSearch(
                typeof event === 'string' ? event : (event?.target?.value ?? '')
              )
            }
            onClear={() => onSearch('')}
          />
          <Dropdown
            id="reconcile-wells-sort"
            size="sm"
            type="inline"
            titleText="Sort"
            hideLabel
            label="Sort"
            items={SORT_ITEMS}
            itemToString={(item) => (item ? item.label : '')}
            selectedItem={SORT_ITEMS.find((item) => item.id === sortMode)}
            onChange={({ selectedItem }) =>
              selectedItem && onSort(selectedItem.id)
            }
          />
        </TableToolbarContent>
      </TableToolbar>
    </>
  );
}

export interface WellConsolidationPanelProps {
  state: WellConsolidationState;
  dispatch: (action: WellConsolidationAction) => void;
  /**
   * Removal, which may need confirming first — the sheet owns that modal because
   * tearing a survivor down also unchecks everything folded beneath it.
   */
  onRequestRemove: (key: string) => void;
}

/**
 * Well Consolidation, laid out as the Entity Consolidation table: survivors at the top
 * with their deduplicated names nested beneath, then the unactioned names (searchable
 * and sortable), and the reconciliation result below. Drag a name row onto a survivor
 * to deduplicate it; the equivalent checkbox path lives on the left panel.
 * @param {WellConsolidationPanelProps} props The model, its dispatch and the remove hook.
 * @return {JSX.Element} The panel.
 */
export function WellConsolidationPanel({
  state,
  dispatch,
  onRequestRemove,
}: WellConsolidationPanelProps) {
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('alpha-asc');
  // Whether removed (hidden) unactioned names are revealed for restoring.
  const [showHidden, setShowHidden] = useState(false);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  const groups = useMemo(() => groupsOf(state), [state]);
  const groupByKey = useMemo(
    () => new Map(groups.map((group) => [group.key, group])),
    [groups]
  );
  const unactionedCount = groups.filter(
    (group) => statusOf(state, group.key) === 'unactioned'
  ).length;

  // Search and sort apply only to unactioned names; survivors and their deduplicated
  // names keep their structural order and always stay visible as drop targets.
  const term = search.trim().toLowerCase();
  const unactioned = useMemo(() => {
    const direction = sortMode === 'alpha-asc' ? 1 : -1;
    return groups
      .filter(
        (group) =>
          statusOf(state, group.key) === 'unactioned' &&
          !state.hiddenKeys.has(group.key) &&
          (term === '' || group.name.toLowerCase().includes(term))
      )
      .sort((a, b) => direction * a.name.localeCompare(b.name));
  }, [groups, state, term, sortMode]);
  const hiddenGroups = groups.filter((group) =>
    state.hiddenKeys.has(group.key)
  );
  const hiddenCount = hiddenGroups.length;

  /**
   * A name dropped onto a survivor folds into it.
   * @param {string} survivorKey The survivor the name was dropped on.
   * @param {DragEvent} event The drop.
   */
  const handleDrop = (survivorKey: string, event: DragEvent) => {
    event.preventDefault();
    setDragOverKey(null);
    const key = event.dataTransfer.getData('text/plain');
    if (key) dispatch({ type: 'drag', key, survivorKey });
  };

  /**
   * A consolidation row for a name.
   * @param {NameGroup} group The distinct well name.
   * @param {boolean} [hidden] Whether the row is a removed name revealed for restoring.
   * @return {JSX.Element} The row.
   */
  const renderRow = (group: NameGroup, hidden = false) => (
    <ConsolidationRow
      key={group.key}
      state={state}
      dispatch={dispatch}
      onRequestRemove={onRequestRemove}
      group={group}
      hidden={hidden}
      dropOver={dragOverKey === group.key}
      onDragOverChange={setDragOverKey}
      onDrop={handleDrop}
    />
  );

  const removedLabel = `${showHidden ? 'Hide' : 'Show'} ${hiddenCount} removed ${
    hiddenCount === 1 ? 'name' : 'names'
  }`;

  return (
    <>
      <div className={styles.panel}>
        <ConsolidationHeader
          distinctCount={groups.length}
          unactionedCount={unactionedCount}
          search={search}
          onSearch={setSearch}
          sortMode={sortMode}
          onSort={setSortMode}
        />

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th scope="col" className={styles.survivorCol}>
                  <span className={styles.thWithTip}>
                    Survivor
                    <Tooltip
                      align="bottom-start"
                      label="Toggling this on for a distinct well name elects it as the survivor — every other name folded into it becomes a duplicate of this record."
                    >
                      <button
                        type="button"
                        className={styles.infoBtn}
                        aria-label="About the Survivor column"
                      >
                        <Information size={16} />
                      </button>
                    </Tooltip>
                  </span>
                </th>
                <th scope="col">Distinct Well Name</th>
                <th scope="col">Well Count</th>
                <th scope="col" className={styles.actionsCol}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {state.survivorKeys.map((survivorKey) => {
                const survivor = groupByKey.get(survivorKey);
                if (!survivor) return null;
                const folded = state.dependents
                  .filter((dep) => dep.survivorKey === survivorKey)
                  .map((dep) => groupByKey.get(dep.key))
                  .filter((group): group is NameGroup => Boolean(group));
                return (
                  <Fragment key={survivorKey}>
                    {renderRow(survivor)}
                    {folded.map((group) => renderRow(group))}
                  </Fragment>
                );
              })}

              {unactioned.map((group) => renderRow(group))}

              {hiddenCount > 0 && (
                <tr className={styles.controlRow}>
                  <td colSpan={4}>
                    <Button
                      kind="ghost"
                      size="sm"
                      onClick={() => setShowHidden((shown) => !shown)}
                    >
                      {removedLabel}
                    </Button>
                  </td>
                </tr>
              )}
              {showHidden &&
                hiddenGroups.map((group) => renderRow(group, true))}
            </tbody>
          </table>
        </div>
      </div>

      <WellConsolidationSurvivors state={state} dispatch={dispatch} />
    </>
  );
}
